/**
 * codeMatching.ts — auto-match + auto-create + import resolution ladder.
 *
 * Internal item codes are SUPPLIER-AGNOSTIC: one internal item can be supplied
 * by many suppliers under different supplier codes. The internal code is a
 * neutral, opaque anchor that never embeds a supplier code or name, so the
 * same physical item bought from two suppliers stays ONE internal item with
 * two cross-references (SupplierPriceBookEntry = SAP Purchasing Info Record).
 *
 * Shape (v1.52.15): `{PREFIX}-{N}` e.g. FIE-234 — a 3-letter company prefix
 * so you can see at a glance it is an internal code. Prefix is configurable
 * (default FIE for FieldInsight). Matching a supplier code to an internal
 * item tries, in order:
 *   1. Remembered per-supplier mapping (SupplierCodeMap)
 *   2. Price-book entry already linked to an internal code
 *   3. Exact product code match (supplier code == internal code)
 *   4. Product whose code ends with the supplier code (suffix match)
 *   5. Fuzzy description match against product catalogue
 *
 * If nothing matches, auto-create MINTS a fresh code with the company prefix.
 */

import type { Product, SupplierPriceBookEntry, POLineType } from '../data/types';

/** Default company prefix — FieldInsight → FIE. Overridable via workspace setting. */
export const DEFAULT_INTERNAL_CODE_PREFIX = 'FIE';
/** First number used when no codes exist for the active prefix. */
const INTERNAL_CODE_SEED = 100;

/**
 * Normalise a company prefix to exactly 3 A–Z letters.
 * "fie" → "FIE", "FieldInsight" → "FIE", "FI" → "FIX" padded? No — pad with X only if short.
 * Empty / invalid → DEFAULT.
 */
export function normalizeInternalCodePrefix(raw?: string | null): string {
  const letters = String(raw ?? '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 3);
  if (letters.length === 3) return letters;
  if (letters.length === 0) return DEFAULT_INTERNAL_CODE_PREFIX;
  // Pad short prefixes so the glanceable 3-letter shape is preserved.
  return (letters + 'XXX').slice(0, 3);
}

/** Highest numeric suffix already used for this prefix (PREFIX-N). */
export function maxInternalCodeNumber(products: Product[], prefix?: string | null): number {
  const pfx = normalizeInternalCodePrefix(prefix);
  const re = new RegExp(`^${pfx}-(\\d+)$`, 'i');
  return products.reduce((m, prod) => {
    const x = re.exec(prod.code);
    return x ? Math.max(m, parseInt(x[1], 10)) : m;
  }, INTERNAL_CODE_SEED);
}

/**
 * Next supplier-agnostic internal item code: FIE-101, FIE-102, …
 * Scans the catalogue for the highest PREFIX-N and increments.
 */
export function mintInternalCode(products: Product[], prefix?: string | null): string {
  const pfx = normalizeInternalCodePrefix(prefix);
  const next = maxInternalCodeNumber(products, pfx) + 1;
  return `${pfx}-${next}`;
}

/** Peek the next code without minting — for Auto-set button labels. */
export function peekNextInternalCode(products: Product[], prefix?: string | null): string {
  return mintInternalCode(products, prefix);
}

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'for', 'to', 'of', 'in', 'on', 'at', 'with', 'per', 'mm', 'x',
]);

function tokenize(text: string): Set<string> {
  const tokens = (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length >= 2 && !STOP_WORDS.has(t));
  return new Set(tokens);
}

/** Jaccard similarity on description tokens — used for import preview suggestions. */
export function fuzzyDescriptionMatch(
  description: string,
  products: Product[],
  minScore = 0.42,
): { product: Product; score: number } | null {
  const query = tokenize(description);
  if (query.size < 2) return null;
  let best: { product: Product; score: number } | null = null;
  for (const p of products) {
    const cand = tokenize(p.description);
    if (cand.size === 0) continue;
    let inter = 0;
    query.forEach(t => { if (cand.has(t)) inter++; });
    const union = query.size + cand.size - inter;
    const score = union > 0 ? inter / union : 0;
    if (score >= minScore && (!best || score > best.score)) {
      best = { product: p, score };
    }
  }
  return best;
}

export interface AutoMatchInput {
  supplierCode: string;
  description?: string;
  supplierName: string;
  products: Product[];
  priceBookEntry?: SupplierPriceBookEntry | null;
  remembered?: string;
  /** Company 3-letter prefix used when minting (e.g. FIE). */
  internalCodePrefix?: string;
}

export interface AutoMatchResult {
  internalCode: string;
  method: 'remembered' | 'pricebook' | 'exact' | 'suffix' | 'description' | 'fuzzyDescription' | 'created';
}

/** Try to find an existing internal code for a supplier code. Returns null if no match. */
export function autoMatchInternalCode(input: AutoMatchInput): AutoMatchResult | null {
  const sc = (input.supplierCode || '').trim();
  if (!sc) {
    const fuzzyOnly = fuzzyDescriptionMatch(input.description || '', input.products);
    if (fuzzyOnly) {
      return { internalCode: fuzzyOnly.product.code, method: 'fuzzyDescription' };
    }
    return null;
  }

  if (input.remembered?.trim()) {
    return { internalCode: input.remembered.trim(), method: 'remembered' };
  }
  if (input.priceBookEntry?.internalCode?.trim()) {
    return { internalCode: input.priceBookEntry.internalCode.trim(), method: 'pricebook' };
  }

  const exact = input.products.find(p => p.code.toLowerCase() === sc.toLowerCase());
  if (exact) return { internalCode: exact.code, method: 'exact' };

  const suffix = input.products.find(p =>
    p.code.toUpperCase().endsWith(sc.toUpperCase()) ||
    p.code.toUpperCase().endsWith(`-${sc.toUpperCase()}`)
  );
  if (suffix) return { internalCode: suffix.code, method: 'suffix' };

  const fuzzy = fuzzyDescriptionMatch(input.description || '', input.products);
  if (fuzzy) return { internalCode: fuzzy.product.code, method: 'fuzzyDescription' };

  const desc = (input.description || '').trim().toLowerCase();
  if (desc.length >= 8) {
    const byDesc = input.products.find(p =>
      p.description.toLowerCase().includes(desc.slice(0, 20)) ||
      desc.includes(p.description.toLowerCase().slice(0, 20))
    );
    if (byDesc) return { internalCode: byDesc.code, method: 'description' };
  }

  return null;
}

/**
 * Build a new internal code when auto-match finds nothing. Supplier-agnostic:
 * the supplierCode / supplierName arguments are retained for call-site
 * compatibility but DELIBERATELY unused — the minted code never embeds them.
 */
export function generateInternalCode(
  _supplierCode: string,
  _supplierName: string,
  products: Product[],
  prefix?: string | null,
): string {
  return mintInternalCode(products, prefix);
}

/** Match or create — the one-shot used by Auto Create & Match. Fuzzy suggestions do not block create. */
export function autoMatchOrCreate(input: AutoMatchInput): AutoMatchResult {
  const matched = autoMatchInternalCode(input);
  if (matched && matched.method !== 'fuzzyDescription') return matched;
  return {
    internalCode: generateInternalCode(
      input.supplierCode,
      input.supplierName,
      input.products,
      input.internalCodePrefix,
    ),
    method: 'created',
  };
}

// ── Import resolution ladder (v1.20) ─────────────────────────────────────────

export type ImportResolutionStatus = 'matched' | 'suggested' | 'new' | 'service';
export type ImportUserAction = 'pending' | 'autoCreate' | 'service' | 'confirmSuggested';

export interface ImportLineState {
  status: ImportResolutionStatus;
  internalCode?: string;
  suggestedInternal?: string;
  suggestedScore?: number;
  matchMethod?: string;
  userAction?: ImportUserAction;
}

const STRONG_METHODS = new Set(['remembered', 'pricebook', 'exact']);

export function classifyImportLine(input: AutoMatchInput): ImportLineState {
  const match = autoMatchInternalCode(input);
  if (match && STRONG_METHODS.has(match.method)) {
    return {
      status: 'matched',
      internalCode: match.internalCode,
      matchMethod: match.method,
      userAction: 'confirmSuggested',
    };
  }
  if (match && ['suffix', 'description', 'fuzzyDescription'].includes(match.method)) {
    return {
      status: 'suggested',
      suggestedInternal: match.internalCode,
      suggestedScore: match.method === 'fuzzyDescription' ? 0.65 : 0.75,
      matchMethod: match.method,
      userAction: 'confirmSuggested',
    };
  }
  return { status: 'new', userAction: 'pending' };
}

export function isServiceLine(line: { lineType?: POLineType }): boolean {
  return line.lineType === 'service';
}

export function needsInternalCode(line: { lineType?: POLineType; inventoryCode?: string }): boolean {
  return line.lineType !== 'service' && !(line.inventoryCode && line.inventoryCode.trim());
}

const SERVICE_DESC_RE = /\b(repair|repairs|labou?r|service|servicing|install|installation|call[- ]?out|callout|fitting|inspection|freight)\b/i;

/**
 * Heuristic for the import preview Type column: a parsed line with no supplier
 * code and a service-like description is pre-classified as a service line.
 */
export function guessLineType(line: { supplierCode?: string; description?: string }): POLineType {
  const sc = (line.supplierCode || '').trim();
  if (!sc && SERVICE_DESC_RE.test(line.description || '')) return 'service';
  return 'product';
}
